import { ICompaniesForDropdown } from '@/models/company/ICompaniesForDropdown';
import { ref } from 'vue';
import ApiService from '@/_services/api.service';
import swal from '@/_helpers/swal';
import { ICompany } from '@/models/company/ICompany';
import {ICompanyWithUserMatricule} from "@/models/companyMatricule/ICompanyWithUserMatricule";

export async function getPaginatedCompanyForDropDown(
    itemsPerPage: number,
    page: number,
    criteria?: string,
    active?: boolean
): Promise<[number, ICompaniesForDropdown[]]> {
    const totalCompaniesRecords = ref<number>(0);
    const companies = Array<ICompaniesForDropdown>()
    const companiesForDropDown = ref<Array<ICompaniesForDropdown>>(companies);

    interface IParams { itemsPerPage: number; page: number; name?: string; active?: boolean; }
    let params: IParams = {
        itemsPerPage: itemsPerPage,
        page: page,
    };
    if (criteria !== undefined && criteria !== '') {
        params = { ...params, name: criteria.trim() };
    }
    if (active !== undefined) {
        params = { ...params, active: active };
    }

    try {
        const { data } = await ApiService.get('companies', {
            headers: { Accept: 'application/ld+json' },
            params: params,
        });
        totalCompaniesRecords.value = data.data['hydra:totalItems'];
        const transformedCompanies: ICompaniesForDropdown[] = data.data['hydra:member']
            .map((item: { uuid: string, name: string }) => ({
                label: item.name,
                value: item.uuid,
            }));
        companiesForDropDown.value.push(...transformedCompanies);
    } catch (error) {
        swal.error();
    }

    return [totalCompaniesRecords.value, companiesForDropDown.value];
}

export async function searchCompanyOptions({ search, page, hasNextPage }) {
    const [totalCompaniesRecordsForDropDown, companiesForDropDown] = await getPaginatedCompanyForDropDown(5, page, search, true);

    const hasItems = companiesForDropDown.length > 0;
    if (hasItems && page !== totalCompaniesRecordsForDropDown) {
        hasNextPage();
    }

    companiesForDropDown.sort((a, b) => a.label.localeCompare(b.label));

    return companiesForDropDown;
}

export async function fetchCompanies(
    itemsPerPage: number,
    page: number,
    criteria?: string,
    name?: string,
    isActive?: boolean
) {
    const isLoading = ref<boolean>(true);
    const totalRecords = ref<number>(0);
    const companies = Array<ICompany>();
    const tableData = ref<Array<ICompany>>(companies);
    let params = {
        itemsPerPage: itemsPerPage,
        page: page,
        name: name,
        isActive: isActive,
    };

    if (criteria !== "") {
        params = { ...params, name: criteria };
    }

    if (isActive !== undefined) {
        params = { ...params, isActive: isActive };
    }

    try {
        const { data } = await ApiService.get("companies", {
            params: params,
            headers: { Accept: "application/ld+json" },
        });
        totalRecords.value = data.data["hydra:totalItems"];
        data.data["hydra:member"].forEach((item, index: number) => {
            item.id = index;
            tableData.value.push(item);
        });
    } catch (error) {
        swal.error();
    }

    isLoading.value = false;
    return [isLoading, totalRecords, tableData];
}

export async function fetchCompanyInfo(companyUuid: string) {
    const companyModel = ref<{ uuid: string; name: string } | null>(null);
    
    try {
        const { data } = await ApiService.get(`/companies/${companyUuid}`, {
            headers: { Accept: 'application/ld+json' }
        });
        
        if (data) {
            companyModel.value = {
                uuid: data.data.uuid,
                name: data.data.name,
            };
        }
    } catch (error) {
        console.error('Erreur lors de la récupération de la société', error);
        swal.error('Impossible de charger les informations de la société.');
    }

    return companyModel;
}

export async function fetchCompaniesWithUserMatricule(
    itemsPerPage: number,
    page: number,
    globalSearch?: null|string,
    sort?: { fieldLabel: string; fieldValue: string; },
    filters?: Record<string, string | number>
): Promise<[boolean, number, ICompanyWithUserMatricule[]]> {
    const isLoading = ref<boolean>(true);
    const totalRecords = ref<number>(0);
    const tableData = ref<Array<ICompanyWithUserMatricule>>([]);
    let params = {
        itemsPerPage: itemsPerPage,
        page: page,
        search: globalSearch,
    };

    if (isNotEmpty(globalSearch)) {
        params = { ...params, search: globalSearch?.trim() };
    }

    if (sort !== undefined) {
        const { fieldValue, fieldLabel } = sort;
        params = {...params, [fieldLabel]: fieldValue};
    }

    if (filters) {
        Object.entries(filters)
            .filter(([key, value]) => isNotEmpty(value))
            .forEach(([key, value]) => {
                params[key] = value;
            });
    }

    try {
        const { data } = await ApiService.get(`admin/companies-with-user-matricule`, {
            headers: { Accept: 'application/ld+json' },
            params: params
        });
        totalRecords.value = data.data['hydra:totalItems'];
        tableData.value = data.data['hydra:member'];
    } catch (error) {
        swal.error('Une erreur est survenue au chargement des sociétés ayant un matricule');
        console.error('Error fetching companies with user matricules:', error);
    }
    isLoading.value = false;

    return [isLoading.value, totalRecords.value, tableData.value];
}

const isNotEmpty = (value: any) => {
    return value !== null && value !== undefined && (typeof value !== 'string' || value.trim() !== '');
}